import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LabProvider, isLegacyStudent } from './LabContext';
import { RequireAuth, RequireStudent, RequireDynamicStudent, NoIndex } from './LabUI';
import './lab.css';

import LabLanding from './pages/LabLanding';
import LabLogin from './pages/LabLogin';
import ChildHome from './pages/ChildHome';
import ChildPlaceholder from './pages/ChildPlaceholder';
// AnitaHome is no longer mounted (Anita now uses the real ChildHome since
// her Week 1 curriculum is authored). Keeping the file in the codebase as
// a reference component; if you want it back, mount it under any path.
// eslint-disable-next-line no-unused-vars
import AnitaHome from './pages/AnitaHome';
import MyPath from './pages/MyPath';
import Lesson from './pages/Lesson';
import PrintWorksheet from './pages/PrintWorksheet';
import ParentDashboard from './pages/ParentDashboard';
import ParentUsers from './pages/ParentUsers';
import ParentChangePassword from './pages/ParentChangePassword';
import KopilkaPage from './pages/KopilkaPage';
import NimnyPrizeShop from './pages/NimnyPrizeShop';
import ParentPrizeRequests from './pages/ParentPrizeRequests';
import Week10QuestMap from './pages/Week10QuestMap';
import NimnyNightRunner from './pages/NimnyNightRunner';
import { useLab } from './LabContext';

// Redirect /lab/me to either the dynamic student home or the placeholder
// when the user has no course template assigned.
const MeRedirector = () => {
  const { user } = useLab();
  if (!user) return <Navigate to="/lab/login" replace />;
  if (user.role === 'parent' || user.role === 'super_admin') {
    return <Navigate to="/lab/parent" replace />;
  }
  if (!user.courseTemplate) {
    return <ChildPlaceholder />;
  }
  if (isLegacyStudent(user.studentId)) {
    return <Navigate to={`/lab/${user.studentId}`} replace />;
  }
  return <Navigate to={`/lab/student/${user.studentId}`} replace />;
};

// Single mount point — all /lab/* routing lives here, isolated from the
// public site. Account/progress state stays in its own provider; only the
// EN/RU locale follows the public LanguageContext. The .lab-root wrapper scopes the
// typography system (Lora + Nunito Sans) to Lab only — public site
// styles are not affected.
const LabApp = () => (
  <LabProvider>
    <NoIndex />
    <div className="lab-root">
      <Routes>
        <Route index element={<LabLanding />} />
        <Route path="login" element={<LabLogin />} />

        {/* Max's learning path */}
        <Route
          path="max"
          element={
            <RequireStudent studentId="max">
              <ChildHome />
            </RequireStudent>
          }
        />
        <Route
          path="max/path"
          element={
            <RequireStudent studentId="max">
              <MyPath />
            </RequireStudent>
          }
        />
        <Route
          path="max/kopilka"
          element={
            <RequireStudent studentId="max">
              <KopilkaPage />
            </RequireStudent>
          }
        />
        <Route
          path="max/week-10/quest"
          element={
            <RequireStudent studentId="max">
              <Week10QuestMap />
            </RequireStudent>
          }
        />
        <Route
          path="max/nimny-night-run"
          element={
            <RequireStudent studentId="max">
              <NimnyNightRunner />
            </RequireStudent>
          }
        />
        <Route
          path="max/:weekId/:dayId"
          element={
            <RequireStudent studentId="max">
              <Lesson />
            </RequireStudent>
          }
        />
        <Route
          path="max/:weekId/:dayId/print"
          element={
            <RequireStudent studentId="max">
              <PrintWorksheet />
            </RequireStudent>
          }
        />
        <Route
          path="max/prize-shop"
          element={
            <RequireStudent studentId="max">
              <NimnyPrizeShop />
            </RequireStudent>
          }
        />

        {/* Anita — parallel curriculum branch. Routes mirror Max so the
            existing student-aware components render Anita's lessons. */}
        <Route
          path="anita"
          element={
            <RequireStudent studentId="anita">
              <ChildHome />
            </RequireStudent>
          }
        />
        <Route
          path="anita/path"
          element={
            <RequireStudent studentId="anita">
              <MyPath />
            </RequireStudent>
          }
        />
        <Route
          path="anita/kopilka"
          element={
            <RequireStudent studentId="anita">
              <KopilkaPage />
            </RequireStudent>
          }
        />
        <Route
          path="anita/week-10/quest"
          element={
            <RequireStudent studentId="anita">
              <Week10QuestMap />
            </RequireStudent>
          }
        />
        <Route
          path="anita/nimny-night-run"
          element={
            <RequireStudent studentId="anita">
              <NimnyNightRunner />
            </RequireStudent>
          }
        />
        <Route
          path="anita/:weekId/:dayId"
          element={
            <RequireStudent studentId="anita">
              <Lesson />
            </RequireStudent>
          }
        />
        <Route
          path="anita/:weekId/:dayId/print"
          element={
            <RequireStudent studentId="anita">
              <PrintWorksheet />
            </RequireStudent>
          }
        />
        <Route
          path="anita/prize-shop"
          element={
            <RequireStudent studentId="anita">
              <NimnyPrizeShop />
            </RequireStudent>
          }
        />

        {/* Parent area */}
        <Route
          path="parent"
          element={
            <RequireAuth role="parent">
              <ParentDashboard />
            </RequireAuth>
          }
        />
        <Route
          path="parent/users"
          element={
            <RequireAuth role="parent">
              <ParentUsers />
            </RequireAuth>
          }
        />
        <Route
          path="parent/prize-requests"
          element={
            <RequireAuth role="parent">
              <ParentPrizeRequests />
            </RequireAuth>
          }
        />
        <Route
          path="parent/account"
          element={
            <RequireAuth>
              <ParentChangePassword />
            </RequireAuth>
          }
        />

        {/* Generic child landing for non-legacy student accounts. We
            redirect them to their dynamic /lab/student/<sid> path. The
            `ChildPlaceholder` is reserved for users without a course
            assignment. */}
        <Route
          path="me"
          element={
            <RequireAuth>
              <MeRedirector />
            </RequireAuth>
          }
        />

        {/* Dynamic per-student routes. Used by every child whose studentId
            is NOT a legacy slug ("max" / "anita"). Components read
            useParams().studentId to scope their progress + Kopilka. */}
        <Route
          path="student/:studentId"
          element={
            <RequireDynamicStudent>
              <ChildHome />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/path"
          element={
            <RequireDynamicStudent>
              <MyPath />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/kopilka"
          element={
            <RequireDynamicStudent>
              <KopilkaPage />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/prize-shop"
          element={
            <RequireDynamicStudent>
              <NimnyPrizeShop />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/week-10/quest"
          element={
            <RequireDynamicStudent>
              <Week10QuestMap />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/nimny-night-run"
          element={
            <RequireDynamicStudent>
              <NimnyNightRunner />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/:weekId/:dayId"
          element={
            <RequireDynamicStudent>
              <Lesson />
            </RequireDynamicStudent>
          }
        />
        <Route
          path="student/:studentId/:weekId/:dayId/print"
          element={
            <RequireDynamicStudent>
              <PrintWorksheet />
            </RequireDynamicStudent>
          }
        />

        {/* Fallback inside /lab */}
        <Route path="*" element={<Navigate to="/lab" replace />} />
      </Routes>
    </div>
  </LabProvider>
);

export default LabApp;
